'use client';

import { createPortal } from 'react-dom';
import { useCallback, useContext, type ReactNode, type RefObject } from 'react';
import { PopoverBox } from './PopoverBox';
import { PopoverHostShownContext } from './PopoverHostShown';
import type { PopoverPlacement } from './popover-position';

export type { PopoverPlacement } from './popover-position';

interface PopoverProps {
  open: boolean;
  anchorRef: RefObject<HTMLElement>;
  placement?: PopoverPlacement;
  /** Names the popover's dialog for assistive technology, e.g. "Settings". */
  label?: string;
  children: ReactNode;
  onClose?: () => void;
  /** Extra classes on the box, e.g. to restyle it through its CSS variables. */
  className?: string;
}

/** A controlled popover beside its anchor: a non-modal dialog. */
export function Popover({ open: requestedOpen, anchorRef, placement = 'top', label, children, onClose, className }: PopoverProps): JSX.Element | null {
  // A popover inside a hidden (or still sliding) panel stays hidden with it.
  const isHostShown = useContext(PopoverHostShownContext);
  const getAnchor = useCallback((): HTMLElement | null => anchorRef.current, [anchorRef]);

  if (!requestedOpen || !isHostShown) {
    return null;
  }

  return createPortal(
    <PopoverBox
      getAnchor={getAnchor}
      placement={placement}
      onOutsidePointerDown={onClose}
      onEscape={onClose}
      className={className}
      role="dialog"
      aria-modal="false"
      aria-label={label}
    >
      {children}
    </PopoverBox>,
    document.body,
  );
}
